import { supabase } from "../../utils/supabaseClient";
import { invokeStaffFunction } from "../../utils/staffEdgeFunctions";

const FUNCTION_NAME = "rockin-ticket-ops";

// SEC-HARD-005 Phase 2C: attendee-facing failure copy for the Road Home LIVE
// update-request form. Every string the attendee can be shown for a failed
// submission is one of these. Raw transport, Supabase, PostgREST, or RPC text
// never reaches the page: submitUpdateRequest() replaces the server's `error`
// field with one of these before returning, so no caller can render it by
// accident. Each one tells the attendee that their message is still there and
// what to do next.
export const UPDATE_REQUEST_MESSAGES = Object.freeze({
  CONNECTION:
    "We’re having trouble reaching the server. Your message is still here — please try again.",
  LINK_INVALID:
    "This update link is no longer valid. Please return to your ticket email, or contact the Road Home Program for help.",
  MESSAGE_REQUIRED: "Enter the update you need before submitting.",
  // SEC-HARD-005 Phase 2C: the server-side throttle refused this submission.
  // The copy says what happened without naming a limit, a counter, or a
  // countdown — the attendee's next action is the same either way, and the
  // exact retry_after_seconds is machine detail, not something to put on
  // screen. It still promises the message is kept, because it is.
  TOO_MANY:
    "You’ve sent several update requests for this RSVP already. Your message is still here — please wait a little while before sending another.",
  UNAVAILABLE:
    "We couldn’t submit your update request just now. Your message is still here — please try again in a moment.",
});

// Bounded: three attempts total, two retries, then stop. The delays are the
// whole backoff schedule, so the loop cannot outlive them.
const UPDATE_REQUEST_RETRY_DELAYS_MS = Object.freeze([400, 1200]);

// Transport conditions only. A gateway that never reached the function, or
// reached it and could not carry the reply back, is safe to retry because the
// RPC coalesces a repeat submission for the same RSVP into the pending request
// instead of writing a second one. Application answers are NOT in this set:
// an invalid or expired manage token, a validation failure, an authorization
// failure, and a server-side RPC error all arrive as an ordinary response or a
// non-transient status and are returned to the attendee on the first attempt.
// SEC-HARD-005 Phase 2C: 429 is deliberately NOT here. A throttled submission
// is the server's own decision, deterministic and durable for the rest of its
// window, so resending it immediately would burn the attendee's next attempts
// against a limit that has not moved and would be the one behavior the throttle
// exists to prevent.
const RETRYABLE_HTTP_STATUSES = new Set([408, 502, 503, 504]);
// The two supabase-js transport classes. FunctionsHttpError is deliberately
// absent: it means the function answered, so its status is the policy above.
const RETRYABLE_ERROR_NAMES = new Set([
  "FunctionsFetchError",
  "FunctionsRelayError",
]);

function trimToken(token) {
  return String(token ?? "").trim();
}

async function readFunctionError(error) {
  if (error?.context && typeof error.context.json === "function") {
    const data = await error.context.json().catch(() => null);
    if (data && typeof data === "object") {
      return data;
    }
  }

  return {
    ok: false,
    code: "REQUEST_ERROR",
    error: error?.message || "Unable to complete ticket operation.",
  };
}

async function invokeTicketOps(action, token, body = {}, invoker) {
  const normalizedToken = trimToken(token);
  if (!normalizedToken) {
    return {
      ok: false,
      found: false,
      code: "VALIDATION_ERROR",
      error: "This link is missing a valid token.",
    };
  }

  const payload = {
    action,
    token: normalizedToken,
    ...body,
  };

  const { data, error } = await invoker(payload);
  if (error) {
    return readFunctionError(error);
  }

  return data ?? {
    ok: false,
    code: "EMPTY_RESPONSE",
    error: "The ticket service returned an empty response.",
  };
}

function invokePublicTicketOps(payload) {
  return supabase.functions.invoke(FUNCTION_NAME, { body: payload });
}

function invokeStaffTicketOps(payload) {
  return invokeStaffFunction(FUNCTION_NAME, payload);
}

export function lookupTicket(token) {
  return invokeTicketOps("lookup_ticket", token, {}, invokePublicTicketOps);
}

export function checkInTicket(token, method = "qr_scan") {
  return invokeTicketOps("check_in_ticket", token, { method }, invokeStaffTicketOps);
}

export function lookupCancelTicket(token) {
  return invokeTicketOps("lookup_cancel_ticket", token, {}, invokePublicTicketOps);
}

export function cancelTicket(token, reason = "") {
  return invokeTicketOps(
    "cancel_ticket",
    token,
    { confirmed: true, reason: String(reason ?? "").trim() },
    invokePublicTicketOps
  );
}

export function lookupManageRsvp(token) {
  return invokeTicketOps("lookup_manage_rsvp", token, {}, invokePublicTicketOps);
}

/**
 * Did the invoke fail in a way that says nothing about whether the request was
 * accepted? Those, and only those, are worth sending again.
 */
function isTransientInvokeError(error) {
  if (!error) return false;

  // The transport classes are checked FIRST, and on their own. supabase-js
  // treats FunctionsFetchError, FunctionsRelayError, and FunctionsHttpError as
  // three distinct classes: the first two mean the request never reached the
  // function, or its reply never came back, and neither says anything about
  // acceptance. `context` on those two carries whatever the transport had at
  // hand — for a relay failure that can include an incidental status that was
  // never an application answer — so reading the status first would let a
  // number that means nothing here reclassify a genuine transport failure as
  // final. Only FunctionsHttpError's status is the server's own answer.
  if (RETRYABLE_ERROR_NAMES.has(String(error?.name ?? ""))) {
    return true;
  }

  const status = Number(error?.context?.status);
  if (Number.isFinite(status)) {
    return RETRYABLE_HTTP_STATUSES.has(status);
  }

  return false;
}

function defaultWait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * True when the server has confirmed it holds the attendee's request — a new
 * pending request, a replay of one it already accepted, or a correction
 * coalesced into one. All three are real server-side confirmations, so all
 * three are success. Nothing here infers success from a failed or missing
 * response.
 */
export function isUpdateRequestAccepted(result) {
  if (!result || result.ok === false || result.found === false) return false;
  return result.accepted === true || result.logged === true;
}

/**
 * The attendee-facing sentence for a submission that did not succeed. Reads the
 * result's `code` only; the server's own text is never returned.
 */
export function describeUpdateRequestFailure(result) {
  if (result?.found === false) {
    return UPDATE_REQUEST_MESSAGES.LINK_INVALID;
  }

  switch (result?.code) {
    case "CONNECTION_ERROR":
      return UPDATE_REQUEST_MESSAGES.CONNECTION;
    case "MESSAGE_REQUIRED":
      return UPDATE_REQUEST_MESSAGES.MESSAGE_REQUIRED;
    // The RPC's throttle refused the submission. It answers `found: true` —
    // the manage link resolved fine, and the attendee must not be told their
    // link is broken — and it is its own code so it gets its own copy rather
    // than falling through to UNAVAILABLE's "try again in a moment".
    case "RATE_LIMITED":
      return UPDATE_REQUEST_MESSAGES.TOO_MANY;
    // VALIDATION_ERROR covers two different failures, and `found` is the
    // discriminator the server already sends. The Edge Function rejects a
    // malformed token, and the RPC rejects a message that normalizes to
    // nothing — the message is validated server-side too, not only in the
    // browser. Only the RPC can have resolved the manage link, so `found: true`
    // means the link worked and the MESSAGE is what failed. Without a resolved
    // link the refusal came from the token, and the attendee needs the
    // link-invalid copy rather than being told to retype a message that was
    // never the problem.
    case "VALIDATION_ERROR":
      return result?.found === true
        ? UPDATE_REQUEST_MESSAGES.MESSAGE_REQUIRED
        : UPDATE_REQUEST_MESSAGES.LINK_INVALID;
    default:
      return UPDATE_REQUEST_MESSAGES.UNAVAILABLE;
  }
}

/**
 * Replace whatever the server said with copy an attendee can act on, keeping
 * the machine-readable fields the page branches on.
 */
function safeUpdateRequestResult(result) {
  if (isUpdateRequestAccepted(result)) {
    return result;
  }

  return {
    ...result,
    ok: false,
    error: describeUpdateRequestFailure(result),
  };
}

/**
 * Submit a Road Home LIVE update request.
 *
 * Transient transport failures are retried a bounded number of times. That is
 * only safe because the RPC serializes on the RSVP and coalesces a repeat into
 * the pending request, so a retry after a lost response is recognized rather
 * than duplicated. A response that the server actually produced — accepted,
 * expired link, validation — is returned as-is on the first attempt.
 *
 * `wait` exists so tests can run the retry schedule without real timers.
 */
export async function submitUpdateRequest(
  token,
  message,
  requesterEmail = "",
  { wait = defaultWait } = {}
) {
  const trimmedMessage = String(message ?? "").trim();
  if (!trimmedMessage) {
    return {
      ok: false,
      code: "MESSAGE_REQUIRED",
      error: UPDATE_REQUEST_MESSAGES.MESSAGE_REQUIRED,
    };
  }

  const normalizedToken = trimToken(token);
  if (!normalizedToken) {
    return {
      ok: false,
      found: false,
      code: "VALIDATION_ERROR",
      error: UPDATE_REQUEST_MESSAGES.LINK_INVALID,
    };
  }

  const payload = {
    action: "submit_update_request",
    token: normalizedToken,
    message: trimmedMessage,
    requester_email: String(requesterEmail ?? "").trim(),
  };

  for (let attempt = 0; ; attempt += 1) {
    const { data, error } = await invokePublicTicketOps(payload);

    if (!error) {
      return safeUpdateRequestResult(
        data ?? { ok: false, code: "EMPTY_RESPONSE" }
      );
    }

    // Anything the server actually answered is the answer. Only a transport
    // failure is worth sending again.
    if (!isTransientInvokeError(error)) {
      return safeUpdateRequestResult(await readFunctionError(error));
    }

    // The schedule is the bound. When it runs out the attempt stops, the
    // attendee keeps their message, and they are told to try again — never told
    // the request succeeded when no server confirmed it.
    const delay = UPDATE_REQUEST_RETRY_DELAYS_MS[attempt];
    if (delay === undefined) {
      return {
        ok: false,
        code: "CONNECTION_ERROR",
        error: UPDATE_REQUEST_MESSAGES.CONNECTION,
      };
    }

    await wait(delay);
  }
}
